import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  OrganizationMembership,
  OrganizationInvitation,
  CreateInvitationDto,
  UpdateMemberRoleDto,
  InvitationsListResponse,
  MembershipsListResponse
} from '../models/team.model';
import { AuthService } from './auth.service';
import { OrganizationContextService } from './organization-context.service';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Team Service
 *
 * Handles team member and invitation management for organizations.
 * Provides API calls for inviting members, managing roles, and tracking invitations.
 *
 * Reference: /agent-os/specs/2026-01-22-user-onboarding/planning/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class TeamService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private organizationContext = inject(OrganizationContextService);
  private apiUrl = environment.apiUrl;

  // Signal-based state management
  private membersSignal = signal<OrganizationMembership[]>([]);
  private invitationsSignal = signal<OrganizationInvitation[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly members = computed(() => this.membersSignal());
  readonly invitations = computed(() => this.invitationsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Computed stats
  readonly activeMembers = computed(() =>
    this.membersSignal().filter(m => !m.deletedAt).length
  );
  readonly pendingInvitations = computed(() =>
    this.invitationsSignal().filter(i => !i.isAccepted && !i.isCancelled && !i.isExpired).length
  );

  /**
   * Load organization memberships
   */
  async loadMembers(organizationId: string, includeInactive = false): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const params: any = {};
      if (includeInactive) {
        params.status = 'all';
      }

      const response = await firstValueFrom(
        this.http.get<ApiResponse<MembershipsListResponse>>(
          `${this.apiUrl}/organizations/${organizationId}/memberships`,
          { params }
        )
      );

      if (response.success && response.data) {
        this.membersSignal.set(response.data.members);
      } else {
        throw new Error(response.error || 'Failed to load members');
      }
    } catch (error: any) {
      console.error('Error loading members:', error);
      this.errorSignal.set(error.message || 'Failed to load members');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Load organization invitations
   */
  async loadInvitations(
    organizationId: string,
    status: 'pending' | 'accepted' | 'cancelled' | 'expired' | 'all' = 'pending'
  ): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const params = { status };

      const response = await firstValueFrom(
        this.http.get<ApiResponse<InvitationsListResponse>>(
          `${this.apiUrl}/organizations/${organizationId}/invitations`,
          { params }
        )
      );

      if (response.success && response.data) {
        this.invitationsSignal.set(response.data.invitations);
      } else {
        throw new Error(response.error || 'Failed to load invitations');
      }
    } catch (error: any) {
      console.error('Error loading invitations:', error);
      this.errorSignal.set(error.message || 'Failed to load invitations');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Create a new invitation
   */
  async createInvitation(
    organizationId: string,
    dto: CreateInvitationDto
  ): Promise<OrganizationInvitation | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<OrganizationInvitation>>(
          `${this.apiUrl}/organizations/${organizationId}/invitations`,
          dto
        )
      );

      if (response.success && response.data) {
        // Add to local state
        this.invitationsSignal.update(invitations => [...invitations, response.data!]);
        return response.data;
      }

      throw new Error(response.error || 'Failed to create invitation');
    } catch (error: any) {
      console.error('Error creating invitation:', error);
      this.errorSignal.set(error.message || 'Failed to create invitation');
      throw error; // Re-throw for component error handling
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Resend an invitation
   */
  async resendInvitation(
    organizationId: string,
    invitationId: string
  ): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<OrganizationInvitation>>(
          `${this.apiUrl}/organizations/${organizationId}/invitations/${invitationId}/resend`,
          {}
        )
      );

      if (response.success && response.data) {
        const data = response.data;
        this.invitationsSignal.update(invitations => {
          const index = invitations.findIndex(i => i.id === invitationId);
          if (index === -1) {
            return invitations;
          }
          const updated = [...invitations];
          updated[index] = {
            ...updated[index],
            resendCount: data.resendCount,
            lastResentAt: data.lastResentAt,
          };
          return updated;
        });
      } else {
        throw new Error(response.error || 'Failed to resend invitation');
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      this.errorSignal.set(error.message || 'Failed to resend invitation');
      throw error;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Cancel an invitation
   */
  async cancelInvitation(
    organizationId: string,
    invitationId: string
  ): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(
          `${this.apiUrl}/organizations/${organizationId}/invitations/${invitationId}`
        )
      );

      if (response.success) {
        // Remove from local state
        this.invitationsSignal.update(invitations =>
          invitations.filter(i => i.id !== invitationId)
        );
      } else {
        throw new Error(response.error || 'Failed to cancel invitation');
      }
    } catch (error: any) {
      console.error('Error cancelling invitation:', error);
      this.errorSignal.set(error.message || 'Failed to cancel invitation');
      throw error;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update member role
   */
  async updateMemberRole(
    organizationId: string,
    profileId: string,
    dto: UpdateMemberRoleDto
  ): Promise<OrganizationMembership | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.put<ApiResponse<OrganizationMembership>>(
          `${this.apiUrl}/organizations/${organizationId}/memberships/${profileId}`,
          dto
        )
      );

      if (response.success && response.data) {
        // Update local state
        this.membersSignal.update(members => {
          const index = members.findIndex(m => m.profileId === profileId);
          if (index !== -1) {
            const updated = [...members];
            updated[index] = response.data!;
            return updated;
          }
          return members;
        });
        return response.data;
      }

      throw new Error(response.error || 'Failed to update member role');
    } catch (error: any) {
      console.error('Error updating member role:', error);
      this.errorSignal.set(error.message || 'Failed to update member role');
      throw error;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Remove (soft delete) a member
   */
  async removeMember(
    organizationId: string,
    profileId: string
  ): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(
          `${this.apiUrl}/organizations/${organizationId}/memberships/${profileId}`
        )
      );

      if (response.success) {
        // Mark as deleted in local state
        this.membersSignal.update(members => {
          const index = members.findIndex(m => m.profileId === profileId);
          if (index !== -1) {
            const updated = [...members];
            updated[index] = {
              ...updated[index],
              deletedAt: new Date().toISOString()
            };
            return updated;
          }
          return members;
        });
      } else {
        throw new Error(response.error || 'Failed to remove member');
      }
    } catch (error: any) {
      console.error('Error removing member:', error);
      this.errorSignal.set(error.message || 'Failed to remove member');
      throw error;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Reactivate a soft-deleted member
   */
  async reactivateMember(
    organizationId: string,
    profileId: string
  ): Promise<OrganizationMembership | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<OrganizationMembership>>(
          `${this.apiUrl}/organizations/${organizationId}/memberships/${profileId}/reactivate`,
          {}
        )
      );

      if (response.success && response.data) {
        // Update local state
        this.membersSignal.update(members => {
          const index = members.findIndex(m => m.profileId === profileId);
          if (index !== -1) {
            const updated = [...members];
            updated[index] = response.data!;
            return updated;
          }
          return members;
        });
        return response.data;
      }

      throw new Error(response.error || 'Failed to reactivate member');
    } catch (error: any) {
      console.error('Error reactivating member:', error);
      this.errorSignal.set(error.message || 'Failed to reactivate member');
      throw error;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Accept an invitation (after user is authenticated)
   */
  async acceptInvitation(invitationId: string): Promise<OrganizationMembership | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<OrganizationMembership>>(
          `${this.apiUrl}/invitations/${invitationId}/accept`,
          {}
        )
      );

      if (response.success && response.data) {
        // Refresh Firebase token to get updated claims including new organization
        console.log('[TeamService] Invitation accepted, refreshing token to get updated claims...');
        await this.authService.refreshToken();
        console.log('[TeamService] Token refreshed successfully');

        // Set organization context to the newly joined organization
        const organizationId = (response.data as any).organizationId;
        if (organizationId) {
          console.log('[TeamService] Setting organization context to:', organizationId);
          this.organizationContext.setCurrentOrganization(organizationId);
          console.log('[TeamService] Organization context set successfully');
        }

        return response.data;
      }

      throw new Error(response.error || 'Failed to accept invitation');
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      this.errorSignal.set(error.message || 'Failed to accept invitation');
      throw error;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /**
   * Reset state (clear all data)
   */
  reset(): void {
    this.membersSignal.set([]);
    this.invitationsSignal.set([]);
    this.errorSignal.set(null);
    this.loadingSignal.set(false);
  }
}
